import { useRef, useCallback, useEffect } from "react";
import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";
import { TranscriptionError } from "shared/errors";
import { phraseService } from "services";

const useAzureSpeech = ({
    onRecognizing,
    onRecognized,
    onSynthesized,
    onStartListening,
    onStopListening,
    onStartSynthesizing,
    onStopSynthesizing,
    onError,
}) => {
    const speechConfigRef = useRef(null);
    const recognizerRef = useRef(null);
    const inputAudioConfigRef = useRef(null);

    const synthesizerRef = useRef(null);
    const outputAudioConfigRef = useRef(null);
    const outputAudioStreamRef = useRef(null);

    const onRecognizingRef = useRef(onRecognizing);
    const onRecognizedRef = useRef(onRecognized);
    const onSynthesizedRef = useRef(onSynthesized);
    const onStartListeningRef = useRef(onStartListening);
    const onStopListeningRef = useRef(onStopListening);
    const onStartSynthesizingRef = useRef(onStartSynthesizing);
    const onStopSynthesizingRef = useRef(onStopSynthesizing);

    const transcript = useRef(null);
    const currentSessionRef = useRef(null);

    // performance helpers
    const micInitializedRef = useRef(false);
    const audioContextRef = useRef(null);
    const isRecognizerWarmRef = useRef(false);

    useEffect(() => {
        onRecognizingRef.current = onRecognizing;
    }, [onRecognizing]);

    useEffect(() => {
        onRecognizedRef.current = onRecognized;
    }, [onRecognized]);

    useEffect(() => {
        onSynthesizedRef.current = onSynthesized;
    }, [onSynthesized]);

    useEffect(() => {
        onStartListeningRef.current = onStartListening;
    }, [onStartListening]);

    useEffect(() => {
        onStopListeningRef.current = onStopListening;
    }, [onStopListening]);

    useEffect(() => {
        onStartSynthesizingRef.current = onStartSynthesizing;
    }, [onStartSynthesizing]);

    useEffect(() => {
        onStopSynthesizingRef.current = onStopSynthesizing;
    }, [onStopSynthesizing]);

    // iOS AudioContext fix
    const resumeAudioContext = async () => {
        if (typeof window === "undefined") return;

        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;

        if (!audioContextRef.current) {
            audioContextRef.current = new AudioContext();
        }

        if (audioContextRef.current.state === "suspended") {
            await audioContextRef.current.resume();
        }
    };

    const updateAuthToken = useCallback((tokenCache) => {
        if (recognizerRef.current) {
            recognizerRef.current.authorizationToken = tokenCache.token;
        }

        if (speechConfigRef.current) {
            speechConfigRef.current.authorizationToken = tokenCache.token;
        }

        if (synthesizerRef.current) {
            synthesizerRef.current.authorizationToken = tokenCache.token;
        }
    }, []);

    const initConfigs = useCallback(async(tokenCache) => {
        if (!speechConfigRef.current) {
            const config = SpeechSDK.SpeechConfig.fromAuthorizationToken(tokenCache.token, tokenCache.region);
            config.enableDictation();

            config.setProperty(
                SpeechSDK.PropertyId.Speech_SegmentationSilenceTimeoutMs,
                '1000'
            );
            config.setProperty(
                SpeechSDK.PropertyId.Speech_SegmentationMaximumTimeMs,
                '70000'
            );
            config.setProperty(
                SpeechSDK.PropertyId.Conversation_Initial_Silence_Timeout,
                0
            );
            config.setProperty(
                SpeechSDK.PropertyId.SpeechServiceConnection_RecoMode,
                "CONVERSATION"
            );

            speechConfigRef.current = config;
        }

        if (!inputAudioConfigRef.current) {
            const stream = await checkMicPermission();   // cached mic
            inputAudioConfigRef.current = SpeechSDK.AudioConfig.fromStreamInput(stream);
        }
    }, []);

    const initRecognizer = useCallback((languages = ["en-US"]) => {
        const autoDetectSourceLanguageConfig = SpeechSDK.AutoDetectSourceLanguageConfig.fromLanguages(languages);

        const recognizer = SpeechSDK.SpeechRecognizer.FromConfig(
            speechConfigRef.current,
            autoDetectSourceLanguageConfig,
            inputAudioConfigRef.current
        );

        recognizer.recognizing = (_, e) => {
            if (onRecognizingRef.current) {
                onRecognizingRef.current({
                    language: e.result.language,
                    text: e.result.text
                });
            }
        };

        recognizer.recognized = async (_, e) => {
            if (e.result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
                await onRecognizedRef.current({
                    language: e.result.language,
                    text: e.result.text
                }, currentSessionRef.current);
                onStopListeningRef.current?.();
            } else if (e.result.reason === SpeechSDK.ResultReason.NoMatch) {
                console.log("No Match: Speech could not be recognized.");
            }
        };

        recognizer.canceled = (_, e) => {
            console.log("cancelled", e);
            onError?.(new TranscriptionError(e));
            stopListening();
        };

        recognizer.sessionStopped = () => {
            stopListening();
        };

        const recognizerConnection = SpeechSDK.Connection.fromRecognizer(recognizer);
        recognizerConnection.openConnection(
            () => { console.log("Recognizer connected"); },
            (err) => { onError?.(err); },
      );

        recognizerRef.current = recognizer;
    }, []);

    // warm-up recognizer
    const warmupRecognizer = useCallback(() => {
        return new Promise((resolve) => {
            if (isRecognizerWarmRef.current) return resolve();

            const recognizer = recognizerRef.current;
            if (!recognizer) return resolve();

            recognizer.startContinuousRecognitionAsync(() => {
                recognizer.stopContinuousRecognitionAsync(() => {
                    isRecognizerWarmRef.current = true;
                    resolve();
                });
            });
        });
    }, []);

    const initSynthesizer = useCallback(() => {
        const stream = SpeechSDK.AudioOutputStream.createPullStream();
        const audioConfig = SpeechSDK.AudioConfig.fromStreamOutput(stream);
        const synthesizer = new SpeechSDK.SpeechSynthesizer(speechConfigRef.current, audioConfig);

        synthesizer.synthesisCompleted = (_, e) => {
            onStopSynthesizingRef.current?.();
        };

        synthesizer.synthesisStarted = (_, e) => {
            onStartSynthesizingRef.current?.();
        };

        const synthesizerConnection = SpeechSDK.Connection.fromSynthesizer(synthesizer);
        synthesizerConnection.openConnection(
            () => { console.log("Synthesizer connected"); },
            (err) => { onError?.(err); },
        );

        outputAudioStreamRef.current = stream;
        outputAudioConfigRef.current = audioConfig;
        synthesizerRef.current = synthesizer;
    }, []);

    const initAzureSpeech = useCallback((languages) => {
        initRecognizer(languages);
        initSynthesizer();
    }, [recognizerRef.current, synthesizerRef.current]);

    const stopListening = useCallback(() => {
        return new Promise((resolve, reject) => {
            const recognizer = recognizerRef.current;
            if (!recognizer) {
                return resolve();
            }

            transcript.current = null;

            recognizer.stopContinuousRecognitionAsync(
                () => {
                    onStopListeningRef.current?.();
                    resolve();
                },
                (err) => {
                    onError?.(err);
                    onStopListeningRef.current?.();
                    reject(err);
                }
            );
        });
    }, []);

    const disposeRecogniser = useCallback(() => {
        const recognizer = recognizerRef.current;

        if (recognizer) {
            if (!recognizer.privDisposed) {
                recognizer.close();
            }
            recognizer.recognizing = undefined;
            recognizer.recognized = undefined;
            recognizer.canceled = undefined;
            recognizer.sessionStopped = undefined;
        }

        recognizerRef.current = null;
    }, []);

    const disposeSynthesizer = useCallback(() => {
        const synthesizer = synthesizerRef.current;
        if (synthesizer) {
            synthesizer.close();
            outputAudioStreamRef.current?.close();
        }

        outputAudioStreamRef.current = null;
        outputAudioConfigRef.current = null;
        synthesizerRef.current = null;
    }, []);

    const disposeAzureSpeech = useCallback(async () => {
        await stopListening();
        disposeRecogniser();
        disposeSynthesizer();
    }, []);

    // mic permission cached
    const checkMicPermission = async () => {
        try {
            if (micInitializedRef.current) return;

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    
            
            // stream.getTracks().forEach(t => t.stop());
    
            micInitializedRef.current = true;
    
            return stream;
        } catch(err) {
            onError?.(err);
        }
    };

    const startListening = useCallback(async () => {
        const recognizer = recognizerRef.current;
        transcript.current = null;
        currentSessionRef.current = Date.now();

        try {
            await resumeAudioContext();   // iOS fix
            // await warmupRecognizer();     // removes first-call lag

            await recognizer?.startContinuousRecognitionAsync(() => {
                onStartListeningRef.current?.();
            });
        } catch (err) {
            onError?.(err);
        }
    }, []);

    const getDynamicProsodyRate = ({ text = "", language = "en-US" }) => {
        const content = String(text).trim();
        const len = content.length;
        const lang = language.split("-")[0];
      
        let rate;
      
        switch (lang) {
          case "bn": // Bangla → keep slow
            rate = -8;
            break;
      
          case "ar": // Arabic → slightly faster
          case "ur": // Urdu → slightly faster
            rate = -4;
            break;
      
          case "de": // German
          case "ru": // Russian
          case "uk": // Ukrainian
            rate = -7;
            break;
      
          case "es": // Spanish
          case "fr": // French
            rate = -6;
            break;
      
          case "sq": // Albanian
          case "uz": // Uzbek
          case "ht": // Haitian Creole
            rate = -5;
            break;
      
          case "zh": // Chinese
          case "ko": // Korean
            rate = -2;
            break;
      
          case "en": // English
          default:
            rate = -4;
        }
      
        // Adjust for text length
        if (len < 30) rate += 2;       // short text → slightly faster
        else if (len > 180) rate -= 2; // very long → slower
        else if (len > 100) rate -= 1; // long → slightly slower
      
        // Dense punctuation → slightly slower
        if (/\d|[,;:]/.test(content)) rate -= 1;
      
        // Clamp to safe range
        if (rate < -10) rate = -10;
        if (rate > 6) rate = 6;
      
        return `${rate}%`;
      };

      const getDynamicProsodyPitch = ({ text = "", language = "en-US" }) => {
        const len = String(text).trim().length;
        const lang = language.split("-")[0];
      
        let pitch;
      
        switch (lang) {
          case "bn":
            pitch = 1; // slightly brighter helps clarity
            break;
      
          case "ar":
          case "ur":
            pitch = -1; // slightly lower
            break;
      
          case "de":
          case "ru":
          case "uk":
            pitch = -1; // slightly lower for long/complex words
            break;
      
          case "es":
          case "fr":
            pitch = 1; // slightly higher for clarity
            break;
      
          case "zh":
          case "ko":
            pitch = 2; // tonal languages benefit from small boost
            break;
      
          default:
            pitch = 0; // English baseline
        }
      
        // Very long text → slightly flatter pitch
        if (len > 180) pitch -= 1;
      
        // Clamp
        if (pitch < -3) pitch = -3;
        if (pitch > 3) pitch = 3;
      
        return `${pitch}%`;
      };

    const synthesizeText = useCallback(({ text, language, voiceName = "en-US-AndrewMultilingualNeural", prosodyRate = "-10%", sessionId = '' }) => {
        prosodyRate = getDynamicProsodyRate({
            text,
            language
          });

        const prosodyPitch = getDynamicProsodyPitch({text, language});

        return new Promise((resolve, reject) => {
            const ssml = `
                <speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" xml:lang="${language}">
                    <voice name="${voiceName}">
                        <prosody rate="${prosodyRate}" pitch="${prosodyPitch}">
                            ${text}
                        </prosody>
                    </voice>
                </speak>
            `;

            synthesizerRef?.current?.speakSsmlAsync(
                ssml,
                async (result) => {
                    if (result.reason === SpeechSDK.ResultReason.SynthesizingAudioCompleted) {
                        const blob = new Blob([result.audioData], { type: "audio/wav" });

                        if (onSynthesizedRef.current) {
                            onSynthesizedRef.current(blob, sessionId);
                        }
                    }
                    resolve();
                },
                error => {
                    onError?.(error);
                    reject(error);
                }
            );
        })
    }, []);

    return {
        initConfigs,
        initAzureSpeech,
        initSynthesizer,
        startListening,
        stopListening,
        synthesizeText,
        disposeAzureSpeech,
        updateAuthToken,
        sessionRef: currentSessionRef,
    };
};

export default useAzureSpeech;
